import './TimeTravelModal.scss';
import { useCallback, useState } from 'react';
import cn from 'classnames';
import { Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdClose, MdHistory } from 'react-icons/md';
import { Button } from '@Components/Button';
import { IconButton } from '@Components/IconButton';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { getFormattedDaysAgo } from '@src/utils/date-utils';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';

interface TimeTravelModalProps {
  prefix: string;
  timeTravelOpen: boolean;
  setTimeTravelOpen: (open: boolean) => void;
  setBranchBuild: (branchBuild: string) => void;
}

export const TimeTravelModal = ({
  prefix,
  timeTravelOpen,
  setTimeTravelOpen,
  setBranchBuild
}: TimeTravelModalProps) => {
  const company = useLocationCompany();
  const [show, setShow] = useState(false);

  // Time travel isn't supported on this page yet; it still works on bpm-frontend's classic page.
  const goToClassicPerformance = useCallback(() => {
    window.location.assign(`${import.meta.env.VITE_BPM_WEB_HOST}/${company}/${prefix}/performance`);
  }, [company, prefix]);
  const [date, setDate] = useState(getFormattedDaysAgo(1));
  const [time, setTime] = useState('00:00:00');

  const handleGo = useCallback(() => {
    setBranchBuild(`v2/${date}T${time}`);
    setShow(false);
    setTimeTravelOpen?.(true);
  }, [date, time, setBranchBuild, setTimeTravelOpen]);

  const handleCancel = useCallback(() => {
    setShow(false);
    setTimeTravelOpen?.(false);
  }, [setTimeTravelOpen]);

  return (
    <>
      <OverlayTrigger
        overlay={
          <Tooltip id='tt'>
            Time Travel isn&apos;t supported on this page yet. Click to open the classic Performance
            page, where time travel is available.
          </Tooltip>
        }
        placement={Placement.BOTTOM}
      >
        <div className='time-travel-button-container'>
          <IconButton
            design={Design.NEUTRAL}
            size={Size.MD}
            aria-label='Time Travel'
            onClick={goToClassicPerformance}
            className={cn('time-travel-button time-travel-button-unsupported', {
              'time-travel-active': timeTravelOpen
            })}
          >
            <MdHistory />
          </IconButton>
        </div>
      </OverlayTrigger>

      <Modal show={show} onHide={handleCancel} centered size='lg' className='time-travel-modal'>
        <Modal.Header closeButton={false}>
          <div className='time-travel-top-row'>
            <Modal.Title>
              <div className='time-travel-title-row'>
                <MdHistory size={48} />
                <span className='time-travel-title'>Time Travel</span>
              </div>
            </Modal.Title>
            <div className='time-travel-close-button-container'>
              <IconButton design={Design.NEUTRAL} onClick={handleCancel}>
                <MdClose />
              </IconButton>
            </div>
          </div>
          <div className='time-travel-description'>
            See what your metrics table was like at any moment in time.
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className='time-travel-inputs'>
            <Form.Group>
              <Form.Label>Date</Form.Label>
              <Form.Control
                type='date'
                value={date}
                onChange={e => {
                  setDate(e.target.value);
                }}
                className='time-travel-input'
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Time (UTC)</Form.Label>
              <div className='time-input-container'>
                <Form.Control
                  type='time'
                  value={time}
                  onChange={e => {
                    setTime(e.target.value);
                  }}
                  className='time-travel-input'
                />
              </div>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance={Appearance.EMPTY} design={Design.NEUTRAL} onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleGo}>Go</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
